import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmailAuthProvider, deleteUser, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import { deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useLanguage } from '../../context/LanguageContext.jsx';
import { useTranslatedText } from '../i18n/useTranslatedText.js';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { isEnglish } = useLanguage();

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [password, setPassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [changeUsername, setChangeUsername] = useState('');
  const [changeAge, setChangeAge] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const profileInfoText = useTranslatedText('Profil Bilgileri');
  const emailText = useTranslatedText('E-posta:');
  const nameText = useTranslatedText('İsim');
  const ageText = useTranslatedText('Yaş');
  const saveText = useTranslatedText('Değişiklikleri Kaydet');
  const cancelText = useTranslatedText('İptal');
  const editText = useTranslatedText('Düzenle');
  const notSetText = useTranslatedText('Belirtilmedi');
  const createdAtText = useTranslatedText('Hesap Oluşturma Tarihi:');
  const lastSignInText = useTranslatedText('Son Giriş Tarihi:');
  const verifiedText = useTranslatedText('E-posta Doğrulandı');
  const notVerifiedText = useTranslatedText('E-posta Doğrulanmadı');
  const resetPasswordText = useTranslatedText('Şifreyi Sıfırla');
  const resetLinkSentText = useTranslatedText('Sıfırlama Bağlantısı Gönderildi');
  const logoutText = useTranslatedText('Çıkış Yap');
  const deleteText = useTranslatedText('Hesabı Sil');
  const loadingText = useTranslatedText('Yükleniyor...');

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/giriş-yap');
        return;
      }

      setUser(currentUser);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setChangeUsername(data.username || '');
        setChangeAge(data.age || '');
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch {
      setError(isEnglish ? 'An error occurred while logging out.' : 'Çıkış yapılırken bir hata oluştu.');
    }
  };

  const handlePasswordReset = async () => {
    if (!user) {
      setError(isEnglish ? 'User not found. Please log in again.' : 'Kullanıcı bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetSent(true);
      setSuccessMessage(
        isEnglish
          ? 'Password reset link sent to your email address.'
          : 'Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.'
      );
      setError(null);
    } catch (err) {
      setError(
        (isEnglish ? 'An error occurred while sending reset email: ' : 'Şifre sıfırlama e-postası gönderilirken hata oluştu: ') +
          err.message
      );
      setSuccessMessage(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user.emailVerified) {
      setError(
        isEnglish
          ? 'You must verify your email before deleting your account.'
          : 'Hesabınızı silmek için önce e-posta adresinizi doğrulamanız gerekir.'
      );
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      await deleteDoc(doc(db, 'users', user.uid));
      await deleteUser(user);
      setSuccessMessage(isEnglish ? 'Your account has been deleted successfully.' : 'Hesabınız başarıyla silindi.');
      navigate('/');
    } catch {
      setError(
        isEnglish
          ? 'Failed to delete account. Please check your password and try again.'
          : 'Hesap silinemedi. Lütfen şifrenizi kontrol edip tekrar deneyin.'
      );
    }
  };

  const updateProfile = async () => {
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const updates = {
        username: changeUsername,
        age: parseInt(changeAge, 10),
      };
      await updateDoc(userRef, updates);
      setUserData((prev) => ({ ...prev, ...updates }));
      setIsEditing(false);
      setSuccessMessage(isEnglish ? 'Profile updated successfully.' : 'Profil başarıyla güncellendi.');
      setError(null);
    } catch {
      setError(isEnglish ? 'Failed to update profile.' : 'Profil güncellenemedi.');
    }
  };

  if (!user) {
    return <div className="profile-loading">{loadingText}</div>;
  }

  return (
    <div className="profile-container">
      <h2>{profileInfoText}</h2>

      <div className="profile-info">
        <div className="info-group">
          <label>{emailText}</label>
          <p>{user.email}</p>
        </div>

        {isEditing ? (
          <>
            <div className="info-group">
              <label>{nameText}</label>
              <input type="text" value={changeUsername} onChange={(e) => setChangeUsername(e.target.value)} className="profile-input" />
            </div>
            <div className="info-group">
              <label>{ageText}</label>
              <input
                type="number"
                value={changeAge}
                onChange={(e) => setChangeAge(e.target.value)}
                min="13"
                max="120"
                className="profile-input"
              />
            </div>
            <button onClick={updateProfile} className="save-profile-btn">{saveText}</button>
            <button onClick={() => setIsEditing(false)} className="cancel-edit-btn">{cancelText}</button>
          </>
        ) : (
          <>
            <div className="info-group">
              <label>{nameText}</label>
              <p>{userData?.username || notSetText}</p>
            </div>
            <div className="info-group">
              <label>{ageText}</label>
              <p>{userData?.age || notSetText}</p>
            </div>
            <button onClick={() => setIsEditing(true)} className="edit-profile-btn">{editText}</button>
          </>
        )}

        <div className="info-group">
          <label>{createdAtText}</label>
          <p>{new Date(user.metadata.creationTime).toLocaleString(isEnglish ? 'en-US' : 'tr-TR')}</p>
        </div>

        <div className="info-group">
          <label>{lastSignInText}</label>
          <p>{new Date(user.metadata.lastSignInTime).toLocaleString(isEnglish ? 'en-US' : 'tr-TR')}</p>
        </div>

        {user.emailVerified ? (
          <div className="verification-status verified">✓ {verifiedText}</div>
        ) : (
          <div className="verification-status not-verified">✗ {notVerifiedText}</div>
        )}
      </div>

      <div className="profile-actions">
        <button className="password-reset-btn" onClick={handlePasswordReset} disabled={resetSent}>
          {resetSent ? resetLinkSentText : resetPasswordText}
        </button>

        <button className="logout-btn" onClick={handleLogout}>{logoutText}</button>

        <button className="delete-account-btn" onClick={() => setShowDeleteConfirm(true)} disabled={!user.emailVerified}>
          {deleteText}
        </button>
      </div>

      {showDeleteConfirm && (
        <div className="delete-confirm">
          <p>
            {isEnglish
              ? 'Are you sure you want to delete your account? This action cannot be undone.'
              : 'Hesabınızı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.'}
          </p>
          <input
            className="passwordinp"
            type="password"
            placeholder={isEnglish ? 'Enter your password' : 'Şifrenizi girin'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button onClick={handleDeleteAccount}>{deleteText}</button>
          <button onClick={() => setShowDeleteConfirm(false)}>{cancelText}</button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
    </div>
  );
};

export default Profile;
